<template>
  <div id="#app">
    <div class="title">
      <span class="gradient-text">Sebastião Carvalho</span>
      <div class="title-underline"></div>
    </div>
    <section class="main-navbar-section">
      <div class="container">
        <CustomNavbar 
          :leftMenuItems="navbarOptions.menuOptionsLeft"
          :rightMenuItems="navbarOptions.menuOptionsRight"
        />
      </div>
    </section>
    <transition name="fade" mode="out-in">
      <router-view />
    </transition>
  </div>
</template>

<script>
import CustomNavbar from './components/CustomNavbar.vue'

export default {
  name: 'App',
  components: {
    CustomNavbar,
  },
  methods: {
    downloadCV(event = null) {
      if (event) {
        event.preventDefault();
      }
      
      // Replace 'path/to/your/cv.pdf' with the actual path to your CV file
      // The CV file should be in your public folder (e.g., public/assets/cv.pdf)
      const cvPath = 'assets/Sebastiao_Carvalho_CV.pdf';
      
      // Create a temporary link element to trigger download
      const link = document.createElement('a');
      link.href = cvPath;
      link.download = 'Sebastiao_Carvalho_CV.pdf'; // This will be the downloaded file name
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    
    vnbItemClicked(text) {
      // Handle CV download specifically
      if (text === 'CV') {
        this.downloadCV();
        return; // Prevent default navigation
      }
      // Handle other navigation clicks if needed
    }
  },
  data() {
    return {
      navbarOptions: {
        elementId: 'main-navbar',
        mobileBreakpoint: 0, // Disable mobile dropdown
        ariaLabelMainNav: 'Main Navigation',
        isUsingVueRouter: true,
        collapseOnClickNavigationBackground: true, // Add this
        showMenuIcon: true, // Add this
        menuOptionsLeft: [
          {
            type : 'link',
            text : 'Home',
            path : '/',
            
          },
          {
            type : 'link',
            text : 'Career',
            path : '/career',
          },
          {
            type : 'link',
            text : 'Academics',
            path : '/academics',
          },
          {
            type : 'link',
            text : 'Projects',
            path : '/projects',
          },
          {
            type : 'link',
            text : 'CV',
            path : '#',
            class: 'cv-download-btn'
          }
        ],
        menuOptionsRight: [
          {
            type : 'link',
            text : '⚡',
            path :  'https://github.com/SebastiaoCarvalho',
            iconClass: 'fab fa-github',
            title: 'GitHub'
          },
          {
            type : 'link',
            text : '💼',
            path : 'https://www.linkedin.com/in/sebasti%C3%A3o-carvalho-6036a026b/',
            iconClass: 'fab fa-linkedin',
            title: 'LinkedIn'
          },
          {
            type : 'link',
            text : '🎮',
            path : 'https://sebastiaocarvalho.itch.io/',
            iconClass: 'fab fa-itch-io',
            title: 'Itch.io'
          }
        ]
      }
    }
  }
}
</script>

<style lang="scss">
// CSS Variables for consistent theming
:root {
  --primary-color: #00d4ff;
  --primary-gradient: linear-gradient(135deg, #00d4ff 0%, #5b73ff 100%);
  --accent-color: #ff6b6b;
  --accent-gradient: linear-gradient(135deg, #ff6b6b 0%, #ffa500 100%);
  --secondary-color: #1a1d23;
  --background-dark: #0f1419;
  --background-darker: #0a0e13;
  --background-card: #1a1d23;
  --text-light: #e6e6e6;
  --text-bright: #ffffff;
  --text-muted: #8892b0;
  --text-dim: #64748b;
  --border-color: #2d3748;
  --border-glow: rgba(0, 212, 255, 0.2);
  --shadow-dark: 0 4px 20px rgba(0, 0, 0, 0.4);
  --shadow-hover: 0 8px 30px rgba(0, 0, 0, 0.6);
  --shadow-glow: 0 0 20px rgba(0, 212, 255, 0.1);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  background: linear-gradient(135deg, var(--background-dark) 0%, var(--background-darker) 100%);
  min-height: 100vh;
}

body {
  background: linear-gradient(135deg, var(--background-dark) 0%, var(--background-darker) 100%);
  min-height: 100vh;
}

#app {
  font-family: 'Inter', Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: var(--text-light);
  margin-top: 60px;
  min-height: 100vh;
  background: linear-gradient(135deg, var(--background-dark) 0%, var(--background-darker) 100%);
}

h1 {
  font-size: 4em;
  font-weight: bold;
}

a {
  color: inherit;
  text-decoration: inherit;
}

.container {
  margin: 0px auto 0px auto;
  padding: 0px 20px 40px 20px;
}

// Enhanced Title Section
.title {
  font-size: 5em;
  font-weight: 800;
  margin-bottom: 1em;
  position: relative;
  display: inline-block;
  animation: fadeInDown 1s ease-out;
  
  .gradient-text {
    background: var(--primary-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;
    display: inline-block;
  }
  
  .title-underline {
    width: 120px;
    height: 4px;
    background: var(--primary-gradient);
    margin: 0.5rem auto 0;
    border-radius: 2px;
    animation: scaleIn 1s ease-out 0.5s both;
  }
}

// Enhanced Navigation Bar
#main-navbar {
  padding: 0 20px;
  background: linear-gradient(135deg, var(--background-card) 0%, #1e2328 100%);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  border-radius: 16px;
  box-shadow: var(--shadow-dark), var(--shadow-glow);
  border: 1px solid var(--border-color);
  backdrop-filter: blur(10px);
  margin: 0 20px;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: var(--shadow-hover), 0 0 30px rgba(0, 212, 255, 0.15);
    transform: translateY(-2px);
    border-color: var(--border-glow);
  }
}

.vnb {
  height: 5em;
  
  .button-red {
    background-color: #ff0000;

    &:hover {
      background-color: darken(#ff0000, 10%);
    }
  }
}

.vnb__collapse-button img {
  width: 24px;
  height: 24px;
  padding-bottom: 3px;
}

.vnb__menu-options {
  margin: 0;
  padding: 0;
  list-style-type: none;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  text-align: center;
  align-items: center;
}

.vnb__menu-options--right {
  margin-left: auto;
  margin-right: 2em;
  padding-left: 10px;
}

.vnb__menu-options--left {
  margin-left: 2em;
  margin-right: auto;
  padding-right: 10px;
}

.vnb__popup__top__close-button {
  padding-top: 10px;
}

.vnb__menu-options__option {
  position: relative;
  
  a {
    font-size: 25px;
    margin-right: 0.5em;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    transition: all 0.3s ease;
    position: relative;
    font-weight: 500;
    
    // CV Download Button Specific Styling
    &.cv-download-btn {
      background: var(--accent-gradient);
      color: var(--text-bright);
      font-weight: 600;
      box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
      border: 2px solid transparent;
      
      &:hover {
        background: linear-gradient(135deg, #ff8a8a 0%, #ffb347 100%);
        transform: translateY(-3px) scale(1.05);
        box-shadow: 0 8px 25px rgba(255, 107, 107, 0.4);
        border-color: rgba(255, 255, 255, 0.2);
      }
      
      &::before {
        content: '📄';
        margin-right: 0.5em;
        font-size: 0.9em;
      }
      
      &::after {
        background: rgba(255, 255, 255, 0.3);
      }
    }
    
    // Icon-specific styling
    i.fab {
      font-size: 1.2em;
      transition: all 0.3s ease;
      
      // Platform-specific hover colors
      &.fa-github:hover {
        color: #333;
        text-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
      }
      
      &.fa-linkedin:hover {
        color: #0077b5;
        text-shadow: 0 0 10px rgba(0, 119, 181, 0.5);
      }
      
      &.fa-itch-io:hover {
        color: #fa5c5c;
        text-shadow: 0 0 10px rgba(250, 92, 92, 0.5);
      }
    }
    
    &:hover:not(.cv-download-btn) {
      background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(91, 115, 255, 0.1) 100%);
      color: var(--text-bright);
      transform: translateY(-2px) scale(1.05);
      box-shadow: 0 4px 15px rgba(0, 212, 255, 0.2);
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 50%;
      transform: translateX(-50%) scaleX(0);
      width: 80%;
      height: 2px;
      background: var(--primary-gradient);
      border-radius: 1px;
      transition: transform 0.3s ease;
    }
    
    &:hover::after {
      transform: translateX(-50%) scaleX(1);
    }
  }
}

.vnb__menu-options__option a:focus {
  outline: none;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.2) 0%, rgba(91, 115, 255, 0.2) 100%);
}

.vnb__popup__bottom__menu-options__option__link {
  padding: 6px 24px;
}

.vnb__popup__bottom__menu-options__option__link:focus {
  border-left: 2px solid var(--primary-color);
}

// Enhanced Transitions
.fade-enter-active,
.fade-leave-active {
  transition: all 0.4s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

// Animations
@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scaleX(0);
  }
  to {
    opacity: 1;
    transform: scaleX(1);
  }
}

// Responsive Design
@media (max-width: 768px) {
  .title {
    font-size: 3.5em;
  }
  
  #main-navbar {
    padding: 0 10px;
    margin: 0 5px;
    flex-direction: column;
    gap: 10px;
  }
  
  .vnb__menu-options--left,
  .vnb__menu-options--right {
    margin: 0;
    justify-content: center;
  }
  
  .vnb__menu-options__option a {
    font-size: 16px;
    margin: 0 0.25em;
    padding: 0.4rem 0.6rem;
    
    i.fab {
      font-size: 1.1em;
    }
    
    &.cv-download-btn {
      font-size: 18px;
      padding: 0.4rem 0.8rem;
      
      &::before {
        font-size: 0.8em;
      }
    }
  }
}

@media (max-width: 480px) {
  .title {
    font-size: 2.5em;
  }
  
  .vnb__menu-options__option a {
    font-size: 18px;
    padding: 0.3rem 0.5rem;
    
    i.fab {
      font-size: 1em;
    }
    
    &.cv-download-btn {
      font-size: 16px;
      padding: 0.3rem 0.6rem;
      
      &::before {
        font-size: 0.7em;
        margin-right: 0.3em;
      }
    }
  }

  // Add this to your existing CSS - more specific targeting
@media screen {
    // Hide hamburger menu
    .vnb__collapse-button {
      display: none !important;
    }
    
    // Hide dropdown popup
    .vnb__popup {
      display: none !important;
    }
    
    // Force show desktop menu items
    .vnb__menu-options {
      display: flex !important;
      visibility: visible !important;
      position: relative !important;
      opacity: 1 !important;
      height: auto !important;
      overflow: visible !important;
    }
    
    .vnb__menu-options--left,
    .vnb__menu-options--right {
      display: flex !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
    
    .vnb__menu-options__option {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
    
    .vnb__menu-options__option a {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
  }
}
</style>